import React, { useState, useEffect, useRef } from 'react';
import Modal from '../../components/Modal';
import { supabase } from '../../utils/supabaseClient';
import { v4 as uuidv4 } from 'uuid';

// --- Sanitizer utilities --- //
function stripHtmlTags(str) {
  if (!str) return '';
  // Remove all tags and stray angle brackets
  return str.replace(/<\/?[^>]+(>|$)/g, '').replace(/[<>]/g, '');
}

function isValidUrl(url) {
  if (!url) return false;
  try {
    const u = new URL(url);
    // Only allow http(s) URLs
    return ['http:', 'https:'].includes(u.protocol);
  } catch {
    return false;
  }
}

// --- Main Component --- //
function CreateProfile({ isOpen, onClose }) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    summary: '',
    company: '',
    author: '',
    domain: '',
    logo: '',
    voice: '',
    audience: '',
  });

  const [errors, setErrors] = useState({});
  const nameRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setForm({
        name: '',
        description: '',
        summary: '',
        company: '',
        author: '',
        domain: '',
        logo: '',
        voice: '',
        audience: '',
      });
      setErrors({});
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let sanitizedValue = stripHtmlTags(value);

    // Further validation for URL fields
    if ((name === 'domain' || name === 'logo') && sanitizedValue) {
      // Remove spaces and any trailing slash/newline
      sanitizedValue = sanitizedValue.trim().replace(/\s/g, '');
    }

    setForm({ ...form, [name]: sanitizedValue });
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleCreate = async () => {
    // Validate URLs
    const domainValid = isValidUrl(form.domain);
    const logoValid = isValidUrl(form.logo);

    const newErrors = {
      name: !form.name,
      description: !form.description,
      summary: !form.summary,
      company: !form.company,
      author: !form.author,
      domain: !form.domain || !domainValid,
      logo: !form.logo || !logoValid,
      voice: !form.voice,
      audience: !form.audience,
    };

    if (Object.values(newErrors).some(Boolean)) {
      setErrors(newErrors);
      nameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    try {
      const { data: userData, error: userError } = await supabase.auth.getUser();
      if (userError || !userData?.user) {
        alert('You must be logged in to create a profile.');
        return;
      }

      const user = userData.user;
      const newProfile = {
        id: uuidv4(),
        user_id: user.id,
        ...form,
      };

      const { error } = await supabase.from('profiles').insert([newProfile]);
      if (error) {
        console.error('Error saving profile:', error);
        alert('Failed to save profile.');
        return;
      }

      window.dispatchEvent(new CustomEvent('profile-created', { detail: newProfile }));
      onClose();
    } catch (err) {
      console.error('Unexpected error:', err);
      alert('Something went wrong.');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create a New Profile"
      actions={[
        { label: 'Create', className: 'btn-outlined', onClick: handleCreate },
        { label: 'Cancel', className: 'btn-outlined', onClick: onClose },
      ]}
    >
      <form className="create-item" autoComplete="off">
        <label ref={nameRef}>
          Profile Name:
          {errors.name && <span className="req-message">Required</span>}
        </label>
        <input
          type="text"
          name="name"
          value={form.name}
          onChange={handleChange}
        />

        <label>
          Profile Description:
          {errors.description && <span className="req-message">Required</span>}
        </label>
        <input
          type="text"
          name="description"
          value={form.description}
          onChange={handleChange}
        />

        <label>
          Domain URL:
          {errors.domain && <span className="req-message">Required & valid https:// URL</span>}
        </label>
        <input
          type="text"
          name="domain"
          value={form.domain}
          onChange={handleChange}
          placeholder="https://example.com"
        />

        <div className="row">
          <div>
            <label>
              Website Summary:
              {errors.summary && <span className="req-message">Required</span>}
            </label>
          </div>
        </div>
        <textarea
          name="summary"
          value={form.summary}
          onChange={handleChange}
          rows={4}
        />

        <label>
          Company/Brand Name:
          {errors.company && <span className="req-message">Required</span>}
        </label>
        <input
          type="text"
          name="company"
          value={form.company}
          onChange={handleChange}
        />

        <label>
          Blog Post Author:
          {errors.author && <span className="req-message">Required</span>}
        </label>
        <input
          type="text"
          name="author"
          value={form.author}
          onChange={handleChange}
        />

        <label>
          Website Logo URL:
          {errors.logo && <span className="req-message">Required & valid https:// URL</span>}
        </label>
        <input
          type="text"
          name="logo"
          value={form.logo}
          onChange={handleChange}
          placeholder="https://example.com/logo"
        />

        <label>
          Voice / Tone:
          {errors.voice && <span className="req-message">Required</span>}
        </label>
        <input
          type="text"
          name="voice"
          value={form.voice}
          onChange={handleChange}
          placeholder="e.g. friendly, professional, witty"
        />

        <label>
          Target Audience:
          {errors.audience && <span className="req-message">Required</span>}
        </label>
        <input
          type="text"
          name="audience"
          value={form.audience}
          onChange={handleChange}
          placeholder="e.g. startup founders, SaaS marketers, Gen Z readers"
        />
      </form>
    </Modal>
  );
}

export default CreateProfile;
